import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Plans from './sign-up/plans';
import FreeAccountTiers from './sign-up/free-account-tiers';
import PersonalInfo from './sign-up/personal-info';
import BillingInfo from './sign-up/billing-info';
import TermsOfService from './sign-up/terms-of-service';

import Actions from '@app/action';
import Helper from '@app/helper';
import Store from '@app/store';
import Config from '@app/config';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import LaunchDarkly from '@module/launchdarkly';

import Button from '@component/form/element/button';
import Form, { validationIndentifiers } from '@component/form/';
import FormNotification from '@component/form/element/formnotification';
import PanelLoading from '@component/panel/loading';

import scriptLoader from '@helper/script-loader';

import { EXPERIMENT_FREE_ACCOUNT } from '@constant/experiments';
import { PLAN_TIER } from '@app/constant/plan-tier';

export default class SignupForm extends Form {
  static propTypes = {
    route: PropTypes.object,
    user: PropTypes.object,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      data: {
        billing: 'yearly',
        full_name: '',
        email: '',
        password: '',
        password_confirm: '',
        company_name: '',
        street_and_number: '',
        city: '',
        zipcode: '',
        country_code: Store.getState().session.region.country,
        vat_number: '',
        terms_of_service: false,
        coupon_code: '',
        convert: '',
        sig: '',
      },
      tier: '',
      country: Store.getState().session.region.country,
    };

    this.routeBackElement = null;
    this.formName = 'signup';
    this.createAccount = this.createAccount.bind(this);
    this.getErrorMessage = this.getErrorMessage.bind(this);
    this.getPricing = this.getPricing.bind(this);
    this.validate = this.validate.bind(this);
    this.validateState = this.validateState.bind(this);
    this.handleRouteBackClick = this.handleRouteBackClick.bind(this);
    this.handleTierSelect = this.handleTierSelect.bind(this);
  }

  componentDidMount() {
    const { route } = Store.getState();
    window.addEventListener('blur', this.validateState, true);
    this.setState({ tier: route.query.tier || '' }, () => {
      this.getPricing();
    });

    // TERNOT, update convert and sig in data state.
    const { convert, sig } = route.query;
    if (convert && sig) {
      this.assignTransferToSignup(convert, sig);
    }

    this.loadStripe();
  }

  async loadStripe() {
    await scriptLoader(Config.stripe.url);
  }

  async assignTransferToSignup(convert, sig) {
    Store.dispatch(
      Actions.Errors.validateForm({ convert, sig }, 'assign_transfer')
    );
    await this.updateValue('convert', convert);
    this.updateValue('sig', sig);
  }

  componentWillUnmount() {
    window.removeEventListener('blur', this.validateState, true);
    this.routeBackElement &&
      this.routeBackElement.removeEventListener(
        'click',
        this.handleRouteBackClick
      );
    Store.dispatch(Actions.UI.resetSubscriptionPricing());
  }

  async getPricing() {
    const { route, session } = Store.getState();
    const coupon = route.query.coupon || '';

    if ('coupon' in route.query) {
      this.updateValue('billing', 'yearly', true);
      await this.updateValue('coupon_code', coupon, true);
      this.validateState();
    }

    Store.dispatch(
      Actions.UI.getSubscriptionPricing({
        country_code:
          this.state.data.country_code || session.region.country || '',
        vat_number: '',
        coupon_code: coupon,
        plan_tier: this.state.tier,
      })
    );
  }

  // Returns the first error that comes up for a field. Remote errors have priority over local errors.
  getError(name) {
    const { pending } = Store.getState();

    // Prevent errors when signup is pending (Because the email can suddenly be claimed.)
    if (pending.signup) {
      return false;
    }

    // Remote errors
    if (
      this.getRemoteError(name) &&
      (this.state.data[name] || name === 'country_code')
    ) {
      return this.getRemoteError(name);
    }

    // Local errors
    if (!document.activeElement) {
      return false;
    }
    if (
      document.activeElement.name === name &&
      this.getLocalError(name) &&
      this.state.data[name]
    ) {
      return true;
    }
    if (
      document.activeElement.name !== name &&
      this.getLocalError(name) &&
      this.state.data[name]
    ) {
      return this.getLocalError(name);
    }

    return false;
  }

  validate(key) {
    const { errors } = this.props;

    // If no value and no remote errors, ignore valid state. (Basically all the 'cannot be blank'-errors)
    if (!this.state.data[key] && errors[this.form] && !errors[this.form][key]) {
      return validationIndentifiers.DONT_CHECK;
    }
    if (this.getError(key)) {
      return validationIndentifiers.NOT_VALID;
    }

    return validationIndentifiers.VALID;
  }

  // Returns an error when the field doesn't pass remote validation
  getRemoteError(name) {
    const { errors } = this.props;
    if (errors[this.formName] && errors[this.formName][name]) {
      const error = errors[this.formName][name][0];

      const errorKey = 'form.account.error.' + name + '.' + error;
      return I18n.t(errorKey);
    }

    return false;
  }

  // Checks for local and remote errors and whether a submit is still pending, is used to (de-)activate submitButton.
  formValidation() {
    const { pending } = this.props;
    const { data } = this.state;

    const userIsInFreeAccountExperimentWithFreeTier = this.getUserIsInFreeAccountExperimentWithFreeTier();

    if (pending[this.formName]) {
      return false;
    }

    for (const key in data) {
      if (
        [
          'vat_number',
          'company_name',
          'billing',
          'coupon_code',
          'convert',
          'sig',
        ].indexOf(key) > -1
      ) {
        continue;
      }
      if (
        userIsInFreeAccountExperimentWithFreeTier &&
        ['city', 'zipcode', 'country_code', 'street_and_number'].includes(key)
      ) {
        continue;
      }
      if (!data[key]) {
        return false;
      }
      if (this.validate(key) === validationIndentifiers.NOT_VALID) {
        return false;
      }
    }

    return true;
  }

  getPlanId(subscriptionPricing) {
    switch (this.state.data.billing) {
      case 'yearly':
        return subscriptionPricing.year.plan_id;
      case 'monthly':
        return subscriptionPricing.month.plan_id;
    }
  }

  // Dispatches the request to create an account.
  async createAccount(e) {
    e.preventDefault();
    if (!this.formValidation()) {
      return this;
    }

    this.track();

    const {
      session,
      ui: { subscriptionPricing },
    } = Store.getState();

    Store.dispatch(Actions.Route.setFormState(false));

    await Store.dispatch(
      Actions.User.signup(this.state.data, {
        plan_id: this.getPlanId(subscriptionPricing),
        trk: Analytics.getReferralKey() || '',
        trk_extra: this.props.route.query.trk_extra,
        wt_sent: Helper.getWTSent(),
        wt_from: Helper.getLastUsedSender(),
        page_version: 30,
        language: session.language,
        plan_tier: this.state.tier,
      })
    );

    if (this.props.user.errors && this.props.user.errors[this.formName]) {
      this.scrollToError();
    }
  }

  track() {
    Analytics.loadTrackingPixel('Wetransfer_Plus_Complete_Order_Conv');
    Analytics.pushDataLayerEvent({
      category: 'Plus',
      action: 'Signup Form Submit',
      label: this.state.data.billing,
      channel: this.props.route.query.trk,
    });
  }

  handleRouteBackClick() {
    // We don't want to display the action bar that ask users to stay or leave after form changes
    // when we navigate back to tiers.
    Store.dispatch(Actions.Route.setFormState(false));
    this.setState({ tier: '' });
    // Hide the route back link
    this.props.setReturnPath('', '');
  }

  handleTierSelect(event) {
    const tier = event.target.dataset.tier;
    const { route, setReturnPath } = this.props;
    setReturnPath(`/plus?trk=${route.query.trk}`, 'Plans');
    this.setState({ tier }, () => {
      this.getPricing();
    });
  }

  handleFreeAccountTierSelect = (event) => {
    const tier = event.target.dataset.tier;

    Analytics.trackStructEvent({
      category: 'plus_page',
      action:
        tier === PLAN_TIER.FREE
          ? 'click_create_an_account'
          : 'click_get_wetransfer_plus',
    });

    this.handleTierSelect(event);
  };

  getUserIsInFreeAccountExperimentWithFreeTier() {
    const { tier } = this.state;

    return (
      LaunchDarkly.getVariant(EXPERIMENT_FREE_ACCOUNT) &&
      tier === PLAN_TIER.FREE
    );
  }

  get userHasValidCoupon() {
    const { route } = this.props;
    const {
      ui: { subscriptionPricing },
    } = Store.getState();
    return (
      'coupon' in route.query && subscriptionPricing.year.coupon_code !== null
    );
  }

  render() {
    const {
      ui: { subscriptionPricing },
    } = Store.getState();

    if (!subscriptionPricing) {
      return <PanelLoading />;
    }

    const { pending, errors, isUnlimitedUKPlusPage } = this.props;
    const { tier } = this.state;

    const userHasSelectedTier = tier !== '';
    const userIsInFreeAccountExperimentWithFreeTier = this.getUserIsInFreeAccountExperimentWithFreeTier();

    if (
      LaunchDarkly.getVariant(EXPERIMENT_FREE_ACCOUNT) &&
      !userHasSelectedTier &&
      !this.userHasValidCoupon
    ) {
      return (
        <FreeAccountTiers onTierSelect={this.handleFreeAccountTierSelect} />
      );
    }

    if (
      LaunchDarkly.getVariant(EXPERIMENT_FREE_ACCOUNT) &&
      userHasSelectedTier
    ) {
      this.routeBackElement = document.getElementsByClassName('panel__back')[0];
      this.routeBackElement &&
        this.routeBackElement.addEventListener(
          'click',
          this.handleRouteBackClick
        );
    }

    const active = this.formValidation();

    let submitText = userIsInFreeAccountExperimentWithFreeTier
      ? "Let's go"
      : I18n.t('form.account.complete_order');
    if (pending[this.formName]) {
      submitText = I18n.t('form.submitting');
    }

    // The convert code-error is an odd one. Since it is stored in a different property in our Error-store and is shouldn't prevent the user from submitting
    const convertError = errors.assign_transfer
      ? [
          <FormNotification
            key="account_error_converter"
            message={I18n.t(
              `form.account.error.convert.${errors.assign_transfer.convert[0]}`
            )}
            type="error"
          />,
          <br key="account_error_break" />,
        ]
      : null;

    let formClassName;
    let title;

    // Items that need to be adjusted when we show the new Plus page
    if (isUnlimitedUKPlusPage) {
      formClassName = 'signup-form signup-form-new';
      title = (
        <h2 className="signup-form-new__title">{I18n.t('form.plan.title')}</h2>
      );
    } else {
      title = (
        <h2 className="signup-form__title">
          {userIsInFreeAccountExperimentWithFreeTier
            ? 'Sign up for a WeTransfer Account'
            : I18n.t('form.account.signup_for_plus')}
        </h2>
      );
    }

    const wrapperClassName = classnames({
      signup: isUnlimitedUKPlusPage,
      'signup-form': !isUnlimitedUKPlusPage,
    });

    return (
      <div className={wrapperClassName}>
        {title}
        <form
          method="post"
          onSubmit={this.createAccount}
          className={formClassName}
        >
          {convertError}
          {!userIsInFreeAccountExperimentWithFreeTier &&
            (subscriptionPricing.year.discount >=
            subscriptionPricing.year.subtotal ? (
              <legend className="signup-form__legend">
                {I18n.t('form.account.legend.free_plus')}
              </legend>
            ) : (
              <Plans
                subscriptionPricing={subscriptionPricing}
                billing={this.state.data.billing}
                userHasValidCoupon={this.userHasValidCoupon}
                onPlanChange={(event) =>
                  this.updateValue('billing', event.target.value)
                }
              />
            ))}
          <PersonalInfo
            fullName={this.state.data.full_name}
            email={this.state.data.email}
            password={this.state.data.password}
            passwordConfirm={this.state.data.password_confirm}
            getErrorMessage={this.getErrorMessage}
            validate={this.validate}
            onFieldChange={(event) =>
              this.updateValue(event.target.name, event.target.value)
            }
          />
          {!userIsInFreeAccountExperimentWithFreeTier && (
            <BillingInfo
              companyName={this.state.data.company_name}
              vatNumber={this.state.data.vat_number}
              countryCode={this.state.data.country_code}
              streetAndNumber={this.state.data.street_and_number}
              city={this.state.data.city}
              zipcode={this.state.data.zipcode}
              country={this.state.country}
              formName={this.formName}
              updateValue={this.updateValue}
              onVatFieldResetError={this.resetError}
              onCountryFieldUpdate={this.getPricing}
              getErrorMessage={this.getErrorMessage}
            />
          )}
          <TermsOfService
            termsOfService={this.state.data.terms_of_service}
            onTermsChange={(event) =>
              this.updateValue(event.target.name, event.target.checked)
            }
            getErrorMessage={this.getErrorMessage}
          />
          <Button
            onClick={this.createAccount}
            active={active}
            text={submitText}
            submit={true}
          />
        </form>
      </div>
    );
  }
}
